import React from 'react';
import {
    LayoutDashboard,
    Users,
    GraduationCap,
    UserCheck,
    Settings,
    LogOut,
    X,
    BookOpen,
    Star,
    Calendar,
    FileText,
    DollarSign,
    Receipt,
    Calculator,
    Award,
    CheckCircle,
    Cog,
    Clock,
    TrendingUp,
    Coins,
    ArrowRightLeft,
    List,
    School,
    Wallet,
    Wifi
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
    currentPath: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, currentPath }) => {
    const { user, signOut } = useAuth();

    const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

    const toggleSubmenu = (name: string) => {
        setExpandedItems(prev =>
            prev.includes(name)
                ? prev.filter(item => item !== name)
                : [...prev, name]
        );
    };

    interface NavigationItem {
        name: string;
        href?: string;
        icon: any;
        children?: NavigationItem[];
    }

    const getNavigationForRole = (role: string): NavigationItem[] => {
        const baseNavigation = [
            { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        ];

        switch (role) {
            case 'admin':
                return [
                    ...baseNavigation,
                    { name: 'Students', href: '/students', icon: GraduationCap },
                    { name: 'Teachers', href: '/teachers', icon: UserCheck },
                    { name: 'Staff', href: '/staff', icon: Users },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                    { name: 'Attendance', href: '/attendance', icon: Clock },
                    { name: 'Student Attendance', href: '/student-attendance-stream', icon: CheckCircle },
                    {
                        name: 'Finance',
                        icon: Receipt,
                        children: [
                            { name: 'Accounts', href: '/finance/accounts', icon: Wallet },
                            { name: 'Petty Cash', href: '/finance/petty-cash', icon: Coins },
                            { name: 'Transaction Ledger', href: '/finance/ledger/transactions', icon: FileText },
                            { name: 'Revenue', href: '/finance/revenue', icon: DollarSign },
                            { name: 'Fees', href: '/finance/fees', icon: Coins },
                        ]
                    },
                    { name: 'Library', href: '/library', icon: BookOpen },
                    { name: 'Device Configuration', href: '/device-configuration', icon: Wifi },
                    { name: 'Class Management', href: '/admin-management', icon: School },
                    { name: 'Settings', href: '/settings', icon: Settings },
                ];
            case 'hr':
                return [
                    ...baseNavigation,
                    { name: 'Staff', href: '/staff', icon: Users },
                    { name: 'Teachers', href: '/teachers', icon: UserCheck },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                    { name: 'Attendance', href: '/attendance', icon: Clock },
                    { name: 'Settings', href: '/settings', icon: Settings },
                ];
            case 'accountant':
                return [
                    ...baseNavigation,
                    {
                        name: 'Finance',
                        icon: Receipt,
                        children: [
                            { name: 'Accounts', href: '/finance/accounts', icon: Wallet },
                            { name: 'Petty Cash', href: '/finance/petty-cash', icon: Coins },
                            { name: 'Transaction Ledger', href: '/finance/ledger/transactions', icon: FileText },
                            { name: 'Revenue', href: '/finance/revenue', icon: DollarSign },
                            { name: 'Fees', href: '/finance/fees', icon: Coins },
                        ]
                    },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                    { name: 'Library', href: '/library', icon: BookOpen },
                    { name: 'Settings', href: '/settings', icon: Settings },
                ];
            case 'teacher':
                return [
                    ...baseNavigation,
                    { name: 'My Classes', href: '/classes', icon: BookOpen },
                    { name: 'Students', href: '/students', icon: GraduationCap },
                    { name: 'Assignments', href: '/assignments', icon: FileText },
                    { name: 'Grades', href: '/grades', icon: Award },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                ];
            case 'student':
                return [
                    ...baseNavigation,
                    { name: 'My Subjects', href: '/subjects', icon: BookOpen },
                    { name: 'Assignments', href: '/assignments', icon: FileText },
                    { name: 'Grades', href: '/grades', icon: Award },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                    { name: 'Attendance', href: '/attendance', icon: CheckCircle },
                ];
            case 'principal':
                return [
                    ...baseNavigation,
                    { name: 'Students', href: '/students', icon: GraduationCap },
                    { name: 'Teachers', href: '/teachers', icon: UserCheck },
                    { name: 'Staff', href: '/staff', icon: Users },
                    { name: 'Schedule', href: '/schedule', icon: Calendar },
                    { name: 'Attendance', href: '/attendance', icon: Clock },
                    { name: 'Library', href: '/library', icon: BookOpen },
                    { name: 'Settings', href: '/settings', icon: Settings },
                ];
            default:
                return baseNavigation;
        }
    };

    const navigation = getNavigationForRole(user?.role || 'admin');

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const renderNavItem = (item: NavigationItem) => {
        const isActive = item.href ? currentPath === item.href : false;
        const isExpanded = expandedItems.includes(item.name);
        const hasChildren = item.children && item.children.length > 0;

        if (hasChildren) {
            return (
                <div key={item.name} className="space-y-1">
                    <button
                        onClick={() => toggleSubmenu(item.name)}
                        className={`
                            w-full sidebar-item justify-between
                            ${isActive || isExpanded ? 'text-gray-900 bg-gray-100' : 'sidebar-item-inactive'}
                        `}
                    >
                        <div className="flex items-center">
                            <item.icon className="h-5 w-5 mr-3" />
                            {item.name}
                        </div>
                        <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>
                    {isExpanded && (
                        <div className="pl-11 space-y-1">
                            {item.children!.map((child) => (
                                <a
                                    key={child.name}
                                    href={child.href}
                                    className={`
                                        block px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150
                                        ${currentPath === child.href
                                            ? 'text-emerald-700 bg-emerald-50'
                                            : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'}
                                    `}
                                >
                                    {child.name}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <a
                key={item.name}
                href={item.href}
                className={`
                    sidebar-item
                    ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}
                `}
            >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
            </a>
        );
    };

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0 lg:h-full
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
                <div className="flex items-center justify-between h-16 px-6 border-b border-white/20 bg-gradient-to-r from-emerald-500 to-teal-600">
                    <div className="flex items-center">
                        <div className="relative">
                            <div className="bg-white/20 backdrop-blur-sm p-2 rounded-xl">
                                <BookOpen className="h-6 w-6 text-white" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center">
                                <Star className="h-2 w-2 text-white" />
                            </div>
                        </div>
                        <div className="ml-3">
                            <span className="text-lg font-bold text-white">Noor-e-Hamza</span>
                            <p className="text-xs text-white/80">Islamic College</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="lg:hidden p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* User info */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <div className="flex items-center">
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-full shadow-lg">
                            <Users className="h-5 w-5 text-white" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{user?.displayName}</p>
                            <p className="text-xs text-emerald-600 capitalize font-medium">{user?.role}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="mt-6 px-3">
                    <div className="space-y-1">
                        {navigation.map(renderNavItem)}
                    </div>
                </nav>

                {/* Sign out button */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                    <button
                        onClick={handleSignOut}
                        className="w-full sidebar-item sidebar-item-inactive text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <LogOut className="h-5 w-5 mr-3" />
                        Sign Out
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
